import { useState, useEffect } from "react";
import {
  Trophy,
  Medal,
  Award,
  Users,
  Target,
  Zap,
  Crown,
  Star,
  ChevronUp,
  ChevronDown,
  Minus,
  Search,
  Calendar,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";
import styles from "./Leaderboard.module.css";

const Leaderboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("overall");
  const [timeframe, setTimeframe] = useState("all-time");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [userRank, setUserRank] = useState(null);

  const tabs = [
    { id: "overall", label: "Overall", icon: Trophy },
    { id: "streak", label: "Streak", icon: Zap },
  ];

  const timeframes = [
    { id: "all-time", label: "All Time" },
    { id: "monthly", label: "This Month" },
    { id: "weekly", label: "This Week" },
    { id: "daily", label: "Today" },
  ];

  useEffect(() => {
    fetchLeaderboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, timeframe]);

  const fetchLeaderboardData = async () => {
    try {
      setLoading(true);

      // Fetch real data from API
      const response = await apiService.leaderboard.getLeaderboard(
        activeTab,
        timeframe,
      );

      if (response && response.success) {
        // Transform API data to match frontend expectations
        const transformedData = response.data.map((entry) => ({
          userId: entry.user_id,
          name: entry.name,
          avatar: entry.avatar,
          score: entry.score || 0,
          assessmentsCompleted: entry.assessments_completed || 0,
          averageScore: entry.average_score || 0,
          streak: entry.streak || 0,
          skillsMastered: entry.skills_mastered || 0,
          change: entry.change || 0,
          badges: entry.badges || 0,
        }));

        setLeaderboardData(transformedData);

        // Set user rank if available
        if (response.userRank) {
          setUserRank({
            userId: response.userRank.user_id,
            name: response.userRank.name,
            avatar: response.userRank.avatar,
            score: response.userRank.score || 0,
            assessmentsCompleted: response.userRank.assessments_completed || 0,
            averageScore: response.userRank.average_score || 0,
            streak: response.userRank.streak || 0,
            skillsMastered: response.userRank.skills_mastered || 0,
            change: response.userRank.change || 0,
            rank: response.userRank.rank || 0,
          });
        }
      } else {
        console.error("Failed to fetch leaderboard:", response?.error);
        // Fallback to mock data if API fails
        const mockData = generateMockLeaderboard();
        setLeaderboardData(mockData);

        const currentUserRank = mockData.findIndex(
          (entry) => entry.userId === user?.id,
        );
        if (currentUserRank !== -1) {
          setUserRank({
            ...mockData[currentUserRank],
            rank: currentUserRank + 1,
          });
        }
      }
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
      // Fallback to mock data on error
      const mockData = generateMockLeaderboard();
      setLeaderboardData(mockData);
    } finally {
      setLoading(false);
    }
  };

  const generateMockLeaderboard = () => {
    const names = [
      "Alex Johnson",
      "Sarah Williams",
      "Michael Chen",
      "Emma Davis",
      "James Wilson",
      "Olivia Brown",
      "Daniel Martinez",
      "Sophia Garcia",
      "William Rodriguez",
      "Isabella Lopez",
      "David Lee",
      "Mia Anderson",
      "Joseph Taylor",
      "Charlotte Thomas",
      "Christopher Moore",
    ];

    return names
      .map((name, index) => ({
        userId: index + 1,
        name,
        avatar: null,
        score: Math.floor(Math.random() * 5000) + 1000,
        assessmentsCompleted: Math.floor(Math.random() * 50) + 10,
        averageScore: Math.floor(Math.random() * 30) + 70,
        streak: Math.floor(Math.random() * 30) + 1,
        skillsMastered: Math.floor(Math.random() * 20) + 5,
        change: Math.floor(Math.random() * 10) - 5,
        badges: Math.floor(Math.random() * 15) + 3,
      }))
      .sort((a, b) => b.score - a.score);
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className={styles.crownIcon} />;
      case 2:
        return <Medal className={styles.silverMedal} />;
      case 3:
        return <Medal className={styles.bronzeMedal} />;
      default:
        return <span className={styles.rankNumber}>{rank}</span>;
    }
  };

  const getRankClass = (rank) => {
    switch (rank) {
      case 1:
        return styles.goldRank;
      case 2:
        return styles.silverRank;
      case 3:
        return styles.bronzeRank;
      default:
        return "";
    }
  };

  const getChangeIndicator = (change) => {
    if (change > 0) {
      return (
        <span className={styles.changeUp}>
          <ChevronUp size={16} />
          {change}
        </span>
      );
    } else if (change < 0) {
      return (
        <span className={styles.changeDown}>
          <ChevronDown size={16} />
          {Math.abs(change)}
        </span>
      );
    }
    return (
      <span className={styles.changeNeutral}>
        <Minus size={16} />
      </span>
    );
  };

  const getMetricValue = (entry) => {
    switch (activeTab) {
      case "overall":
        return `${entry.score.toLocaleString()} pts`;
      case "assessments":
        return `${entry.assessmentsCompleted} completed`;
      case "skills":
        return `${entry.skillsMastered} skills`;
      case "streak":
        return `${entry.streak} days`;
      case "recent":
        return `${entry.averageScore}% avg`;
      default:
        return entry.score;
    }
  };

  const filteredData = leaderboardData.filter((entry) =>
    entry.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className={styles.leaderboardPage}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <Trophy className={styles.headerIcon} />
            <div>
              <h1>Leaderboard</h1>
              <p>Compete with learners worldwide and climb to the top!</p>
            </div>
          </div>
          <div className={styles.headerStats}>
            <div className={styles.statCard}>
              <Users size={20} />
              <div>
                <div className={styles.statValue}>
                  {leaderboardData.length.toLocaleString()}
                </div>
                <div className={styles.statLabel}>Active Learners</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Rank Card */}
      {userRank && (
        <div className={styles.userRankCard}>
          <div className={styles.userRankContent}>
            <div className={styles.userRankLeft}>
              <div className={styles.userRankBadge}>Your Rank</div>
              <div className={styles.userRankPosition}>
                {getRankIcon(userRank.rank)}
                <span className={styles.userRankNumber}>#{userRank.rank}</span>
              </div>
            </div>
            <div className={styles.userRankStats}>
              <div className={styles.userRankStat}>
                <span className={styles.userRankStatLabel}>Score</span>
                <span className={styles.userRankStatValue}>
                  {userRank.score.toLocaleString()}
                </span>
              </div>
            </div>
            <div className={styles.userRankChange}>
              {getChangeIndicator(userRank.change)}
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className={styles.controls}>
        <div className={styles.controlsInner}>
          <div className={styles.tabs}>
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`${styles.tab} ${
                    activeTab === tab.id ? styles.tabActive : ""
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <IconComponent size={18} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          <div className={styles.filters}>
            <div className={styles.searchBox}>
              <Search size={18} />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className={styles.timeframeSelector}>
              <Calendar size={18} />
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
              >
                {timeframes.map((tf) => (
                  <option key={tf.id} value={tf.id}>
                    {tf.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Leaderboard Table */}
      <div className={styles.leaderboardContainer}>
        <div className={styles.leaderboardInner}>
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <p>Loading leaderboard...</p>
            </div>
          ) : (
            <div className={styles.leaderboardTable}>
              {/* Top 3 Podium */}
              {filteredData.length >= 3 && (
                <div className={styles.podium}>
                  {/* 2nd Place */}
                  <div
                    className={`${styles.podiumPlace} ${styles.secondPlace}`}
                  >
                    <div className={styles.podiumRankBadge}>2</div>
                    <div className={styles.podiumRank}>
                      <Medal className={styles.silverMedal} />
                    </div>
                    <div className={styles.podiumAvatar}>
                      {filteredData[1].avatar ? (
                        <img src={filteredData[1].avatar} alt="" />
                      ) : (
                        <div className={styles.avatarPlaceholder}>
                          {filteredData[1].name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className={styles.podiumName}>
                      {filteredData[1].name}
                    </div>
                    <div className={styles.podiumScore}>
                      {getMetricValue(filteredData[1])}
                    </div>
                  </div>

                  {/* 1st Place */}
                  <div className={`${styles.podiumPlace} ${styles.firstPlace}`}>
                    <div className={styles.podiumRankBadge}>1</div>
                    <div className={styles.podiumRank}>
                      <Crown className={styles.crownIcon} />
                    </div>
                    <div className={styles.podiumAvatar}>
                      {filteredData[0].avatar ? (
                        <img src={filteredData[0].avatar} alt="" />
                      ) : (
                        <div className={styles.avatarPlaceholder}>
                          {filteredData[0].name.charAt(0)}
                        </div>
                      )}
                      <div className={styles.championBadge}>
                        <Star size={16} />
                      </div>
                    </div>
                    <div className={styles.podiumName}>
                      {filteredData[0].name}
                    </div>
                    <div className={styles.podiumScore}>
                      {getMetricValue(filteredData[0])}
                    </div>
                  </div>

                  {/* 3rd Place */}
                  <div className={`${styles.podiumPlace} ${styles.thirdPlace}`}>
                    <div className={styles.podiumRankBadge}>3</div>
                    <div className={styles.podiumRank}>
                      <Medal className={styles.bronzeMedal} />
                    </div>
                    <div className={styles.podiumAvatar}>
                      {filteredData[2].avatar ? (
                        <img src={filteredData[2].avatar} alt="" />
                      ) : (
                        <div className={styles.avatarPlaceholder}>
                          {filteredData[2].name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className={styles.podiumName}>
                      {filteredData[2].name}
                    </div>
                    <div className={styles.podiumScore}>
                      {getMetricValue(filteredData[2])}
                    </div>
                  </div>
                </div>
              )}

              {/* Rest of the list */}
              <div className={styles.leaderboardList}>
                {filteredData.slice(3).map((entry, index) => {
                  const rank = index + 4;
                  const isCurrentUser = entry.userId === user?.id;

                  return (
                    <div
                      key={entry.userId}
                      className={`${styles.leaderboardRow} ${
                        isCurrentUser ? styles.currentUser : ""
                      } ${getRankClass(rank)}`}
                    >
                      <div className={styles.rankCell}>{getRankIcon(rank)}</div>

                      <div className={styles.userCell}>
                        <div className={styles.userAvatar}>
                          {entry.avatar ? (
                            <img src={entry.avatar} alt="" />
                          ) : (
                            <div className={styles.avatarPlaceholder}>
                              {entry.name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div className={styles.userName}>
                          {entry.name}
                          {isCurrentUser && (
                            <span className={styles.youBadge}>You</span>
                          )}
                        </div>
                      </div>

                      <div className={styles.statsCell}>
                        <div className={styles.statItem}>
                          <Target size={14} />
                          <span>{entry.assessmentsCompleted}</span>
                        </div>
                        <div className={styles.statItem}>
                          <Award size={14} />
                          <span>{entry.skillsMastered}</span>
                        </div>
                        <div className={styles.statItem}>
                          <Zap size={14} />
                          <span>{entry.streak}d</span>
                        </div>
                      </div>

                      <div className={styles.scoreCell}>
                        <div className={styles.scoreValue}>
                          {getMetricValue(entry)}
                        </div>
                      </div>

                      <div className={styles.changeCell}>
                        {getChangeIndicator(entry.change)}
                      </div>
                    </div>
                  );
                })}
              </div>

              {filteredData.length === 0 && (
                <div className={styles.emptyState}>
                  <Users size={48} />
                  <h3>No users found</h3>
                  <p>Try adjusting your search or filters</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
